/**
 * Config Hot Reload Utilities
 * 
 * Enables live config editing without page reload.
 * Used by the config editor to push updates to preview iframes.
 */

export interface ConfigUpdateMessage {
  type: 'TAGADAPAY_CONFIG_UPDATE';
  config: Record<string, any>;
  timestamp?: number;
}

/**
 * Send config update to target window (iframe, popup, etc.)
 * 
 * @param targetWindow - Window to send update to (e.g., iframe.contentWindow)
 * @param config - Updated config object
 * @param targetOrigin - Target origin (default: '*' for same-origin)
 */
export function sendConfigUpdate(
  targetWindow: Window,
  config: Record<string, any>,
  targetOrigin = '*'
) {
  const message: ConfigUpdateMessage = {
    type: 'TAGADAPAY_CONFIG_UPDATE',
    config,
    timestamp: Date.now(),
  };

  targetWindow.postMessage(message, targetOrigin);
}

/**
 * Send config update to all iframes on the page
 * Useful for config editor with multiple preview panes
 * 
 * @param config - Updated config object
 * @param selector - CSS selector for iframes (default: 'iframe')
 */
export function broadcastConfigUpdate(
  config: Record<string, any>,
  selector = 'iframe'
) {
  if (typeof document === 'undefined') return;

  const iframes = document.querySelectorAll<HTMLIFrameElement>(selector);
  
  iframes.forEach((iframe) => {
    if (iframe.contentWindow) {
      sendConfigUpdate(iframe.contentWindow, config);
    }
  });

  console.log(`[ConfigHotReload] Broadcasted config update to ${iframes.length} iframe(s)`);
}

/**
 * Setup a listener for config updates
 * Returns cleanup function
 * 
 * @param callback - Function to call when config is updated
 */
export function onConfigUpdate(
  callback: (config: Record<string, any>) => void
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleMessage = (event: MessageEvent) => {
    if (event.data?.type === 'TAGADAPAY_CONFIG_UPDATE') {
      callback(event.data.config);
    }
  };

  window.addEventListener('message', handleMessage);

  return () => {
    window.removeEventListener('message', handleMessage);
  };
}

/**
 * Debounce helper for config updates
 * Prevents too many updates in quick succession
 */
export function debounceConfigUpdate(
  fn: (config: Record<string, any>) => void,
  delay = 150
): (config: Record<string, any>) => void {
  let timeoutId: NodeJS.Timeout | null = null;

  return (config: Record<string, any>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      fn(config);
      timeoutId = null;
    }, delay);
  };
}







